import { UISref } from "@uirouter/react";
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Subject } from "rxjs";
import { switchMap } from "rxjs/operators";
import styled from "styled-components";
import {
  Button,
  DangerButton,
  PrimaryButton,
} from "../../../../../components/button";
import { Icon } from "../../../../../components/icons";
import { GlobalOverlayContext } from "../../../../../components/overlay/context";
import { Table } from "../../../../../components/table";
import { isFetchableSuccess } from "../../../../../store/common/fetchable/helpers";
import { UsersDTO } from "../../../../../store/users/models/dto";
import { UsersService } from "../../../../../store/users/service";
import { colours } from "../../../../../styles/colours";
import { UsersRoutes } from "../../../routes";
import { StyledWidget } from "../../common/components";
import { AddUserModal } from "./add-modal";
import { CreateUserModal } from "./create-modal";
import { RemoveUserModal } from "./remove-modal";

const StyledButton = styled(Button)`
  & + & {
    margin-left: 1em;
  }
`;

export const DelegateUsers: FunctionComponent<{
  item: UsersDTO.Responses.GetSingle.T;
}> = ({ item }) => {
  const { userReferenceId } = item;
  const { setOverlay } = useContext(GlobalOverlayContext);

  const [data, setData] = useState<UsersDTO.Responses.GetAll.T>();

  const refresh = useMemo(() => new Subject<void>(), []);

  useEffect(() => {
    const sub = refresh
      .pipe(
        switchMap(() =>
          UsersService.getAllDelegateUsers(userReferenceId, {
            pagination: { page: 0, size: 1000 },
          })
        )
      )
      .subscribe((fetchable) => {
        if (isFetchableSuccess(fetchable)) {
          setData(fetchable.data);
        }
      });
    return () => sub.unsubscribe();
  }, [userReferenceId]);

  useEffect(() => {
    refresh.next();
  }, []);

  const openAddUserModal = useCallback(() => {
    setOverlay({
      title: "Add Delegate User",
      children: (
        <AddUserModal
          userReferenceId={userReferenceId}
          existingDelegates={data?.content?.map((x) => x.userReferenceId) || []}
          refresh={refresh}
        />
      ),
    });
  }, [userReferenceId, data?.content, refresh]);

  const openCreateUserModal = useCallback(() => {
    setOverlay({
      title: "Create Delegate User",
      children: <CreateUserModal userReferenceId={userReferenceId} />,
    });
  }, [userReferenceId]);

  const openRemoveUserModal = useCallback(
    (delegateUserId: string) => {
      setOverlay({
        title: "Remove Delegate User",
        children: (
          <RemoveUserModal
            userReferenceId={userReferenceId}
            delegateUserId={delegateUserId}
          />
        ),
      });
    },
    [userReferenceId]
  );

  return data ? (
    <StyledWidget
      title="Delegate Users"
      adornment={
        <>
          <StyledButton onClick={openAddUserModal} variant="contained">
            Add User
          </StyledButton>
          <StyledButton onClick={openCreateUserModal} variant="contained">
            Create User
          </StyledButton>
        </>
      }
    >
      <Table
        data={data.content}
        head={[
          { name: "firstName", title: "First Name" },
          { name: "lastName", title: "Last Name" },
          { name: "emailAddress", title: "E-mail" },
          { name: "userReferenceId", title: "Delete" },
          { name: "dateOfBirth", title: "", centered: true },
        ]}
        renderFields={{
          userReferenceId: ({ userReferenceId }) => (
            <DangerButton
              onClick={() => openRemoveUserModal(userReferenceId)}
              variant="contained"
            >
              <Icon type="trash" />
            </DangerButton>
          ),
          dateOfBirth: ({ userReferenceId }) => (
            <UISref
              to={UsersRoutes.names.single}
              params={{ id: userReferenceId }}
            >
              <a>
                <PrimaryButton variant="contained">
                  <Icon
                    type="export"
                    rotateAmount={270}
                    colour1={colours.white}
                  />
                </PrimaryButton>
              </a>
            </UISref>
          ),
        }}
        unsortable={["firstName", "lastName"]}
      ></Table>
    </StyledWidget>
  ) : null;
};
